import React, { useMemo } from 'react';
import { ActivityIndicator, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useRiskMonitor } from '../../services/queries/useRiskMonitor';
import { Card } from '../ui/Card';

const pct = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined ? '—' : `${(value * 100).toFixed(digits)}%`;

const pp = (value: number | null | undefined, digits = 1) =>
  value === null || value === undefined ? '—' : `${(value * 100).toFixed(digits)}pp`;

const money = (
  value: number | null | undefined,
  currency: string | null | undefined,
  locale: string | undefined
) => {
  if (value === null || value === undefined) return '—';
  if (!currency) return value.toLocaleString(locale);
  return value.toLocaleString(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  });
};

export function PositionRiskCard({ positionId }: { positionId: number }) {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const { data, isLoading, notReady, error } = useRiskMonitor(positionId);
  const locale = i18n.resolvedLanguage || undefined;

  if (isLoading) {
    return (
      <Card style={styles.card}>
        <ActivityIndicator color={theme.colors.primary} />
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={styles.card}>
        <Text style={styles.title}>
          {t('riskMonitor.positionCardTitle', { defaultValue: 'Position risk' })}
        </Text>
        <Text style={styles.warning}>
          {t('riskMonitor.loadError', {
            defaultValue: 'Risk monitor could not be loaded. Please try again later.',
          })}
        </Text>
      </Card>
    );
  }

  if (notReady || !data) {
    return (
      <Card style={styles.card}>
        <Text style={styles.title}>
          {t('riskMonitor.positionCardTitle', { defaultValue: 'Position risk' })}
        </Text>
        <Text style={styles.muted}>
          {t('riskMonitor.notReady', {
            defaultValue: 'No risk monitor yet — calculated in the weekly review.',
          })}
        </Text>
      </Card>
    );
  }

  if (data.degraded) {
    const reason = data.degraded_reason || 'unknown';
    return (
      <Card style={styles.card}>
        <Text style={styles.title}>
          {t('riskMonitor.positionCardTitle', { defaultValue: 'Position risk' })}
        </Text>
        <Text style={styles.warning}>
          {t(`riskMonitor.degradedReason_${reason}`, {
            defaultValue: 'Risk measurements could not be calculated.',
          })}
        </Text>
      </Card>
    );
  }

  const position = data.position;
  const exclusion = data.coverage.excluded?.find(
    (item) => position.instrument_id != null && item.instrument_id === position.instrument_id
  );
  const breaches = data.position_policy.breaches?.filter(
    (breach) => breach.symbol === position.symbol
  );
  const updatedAt = new Date(data.computed_at).toLocaleDateString(locale);

  return (
    <Card style={styles.card}>
      <Text style={styles.title}>
        {t('riskMonitor.positionCardTitle', { defaultValue: 'Position risk' })}
      </Text>

      {position.covered === false || exclusion ? (
        <>
          <Text style={styles.warning}>
            {t('riskMonitor.positionNotIncluded', {
              defaultValue: 'Not included in this ES estimate',
            })}
          </Text>
          <Text style={styles.measurement}>
            {exclusion?.reason === 'window_truncating'
              ? t('riskMonitor.windowTruncatingReason', {
                  defaultValue: 'Excluded to preserve at least 500 shared trading days.',
                })
              : t('riskMonitor.thinHistoryReason', {
                  defaultValue: 'Too few usable price bars',
                })}
          </Text>
          {exclusion?.reason === 'thin_history' &&
            exclusion.available_bars != null &&
            exclusion.required_bars != null && (
              <Text style={styles.muted}>
                {t('riskMonitor.usableBars', {
                  defaultValue: 'Usable price bars: {{available}} / {{required}}',
                  available: exclusion.available_bars,
                  required: exclusion.required_bars,
                })}
              </Text>
            )}
          {(position.value_base ?? exclusion?.value_base) != null && (
            <Text style={styles.muted}>
              {t('riskMonitor.positionValue', {
                defaultValue: 'Position value: {{value}}',
                value: money(
                  position.value_base ?? exclusion?.value_base,
                  data.base_currency,
                  locale
                ),
              })}
            </Text>
          )}
        </>
      ) : (
        <>
          <View style={styles.metricRow}>
            <View style={styles.metricCell}>
              <Text style={styles.metricValue}>{pct(position.current_weight)}</Text>
              <Text style={styles.metricLabel}>
                {t('riskMonitor.currentWeightLabel', {
                  defaultValue: 'Current portfolio weight',
                })}
              </Text>
            </View>
            <View style={styles.metricCell}>
              <Text style={styles.metricValue}>{pp(position.contribution_pp)}</Text>
              <Text style={styles.metricLabel}>
                {t('riskMonitor.contributionToEs', {
                  defaultValue: 'Contribution to portfolio ES',
                })}
              </Text>
            </View>
          </View>
          <Text style={styles.measurement}>
            {t('riskMonitor.positionShare', {
              defaultValue: '{{value}} of portfolio ES',
              value: pct(position.signed_net_share),
            })}
          </Text>
        </>
      )}

      {breaches?.map((breach) => (
        <Text key={`${breach.symbol}-${breach.current_weight}`} style={styles.warning}>
          {t('riskMonitor.policyBreach', {
            defaultValue: '{{symbol}} is {{current}} versus your {{limit}} single-position policy.',
            symbol: breach.symbol,
            current: pct(breach.current_weight),
            limit: pct(breach.limit),
          })}
        </Text>
      ))}

      <Text style={styles.muted}>
        {t('riskMonitor.portfolioCoverageFootnote', {
          defaultValue: 'Portfolio estimate coverage: {{value}}',
          value: pct(data.coverage.covered_value_fraction),
        })}
      </Text>
      <Text style={styles.muted}>
        {t('riskMonitor.updated', { defaultValue: 'Updated {{date}}', date: updatedAt })}
      </Text>
    </Card>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    card: {
      marginHorizontal: theme.spacing.md,
      marginTop: theme.spacing.sm,
      padding: theme.spacing.md,
    },
    title: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeMd,
      fontWeight: theme.typography.fontWeightSemibold,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
      marginBottom: theme.spacing.md,
    },
    metricRow: { flexDirection: 'row', gap: theme.spacing.xl },
    metricCell: { flex: 1 },
    metricValue: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeXl,
      fontWeight: theme.typography.fontWeightBold,
    },
    metricLabel: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeXs,
    },
    measurement: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizeSm,
      marginTop: theme.spacing.sm,
    },
    muted: {
      color: theme.colors.textSecondary,
      fontSize: theme.typography.fontSizeXs,
      marginTop: theme.spacing.xxs,
    },
    warning: {
      color: theme.colors.warning,
      fontSize: theme.typography.fontSizeXs,
      marginTop: theme.spacing.xs,
    },
  });
}
